import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Download, Smartphone, Monitor, Apple, Chrome, Share2, CheckCircle, ArrowRight } from 'lucide-react';
import { Logo1ClinHorizontal, Logo1ClinIcon } from '../components/Logo1Clin';

export default function InstallAppPage() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installStarted, setInstallStarted] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Detect device
    const ua = navigator.userAgent;
    const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroidDevice = /Android/.test(ua);
    
    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', () => setIsInstalled(true));

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      setInstallStarted(true);
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
      setInstallStarted(false);
    }
  };

  const features = [
    { icon: '⚡', title: 'Lightning Fast', desc: 'Native app-like performance' },
    { icon: '📴', title: 'Works Offline', desc: 'Access data without internet' },
    { icon: '🔔', title: 'Push Notifications', desc: 'Never miss appointments' },
    { icon: '🔒', title: 'Secure & Private', desc: 'Your data stays protected' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-teal-900 to-slate-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/5"></div>
        <div className="max-w-4xl mx-auto px-4 py-16 relative z-10">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <Logo1ClinHorizontal width={320} />
          </div>

          {/* Main Heading */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{fontFamily: 'Manrope'}}>
              Get the 1Clin App
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              AI-powered clinic management at your fingertips. Install now for the best experience.
            </p>
          </div>

          {/* Install Card */}
          <Card className="max-w-md mx-auto bg-white/95 backdrop-blur shadow-2xl border-0 mb-12">
            <CardContent className="p-8">
              {isInstalled ? (
                <div className="text-center">
                  <div className="flex justify-center mb-4">
                    <Logo1ClinIcon size={64} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">App Installed!</h3>
                  <p className="text-slate-600 mb-4">1Clin is ready to use on your device.</p>
                  <Button 
                    onClick={() => window.location.href = '/'}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    Open App <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              ) : isIOS ? (
                <div className="text-center">
                  <div className="flex justify-center mb-4">
                    <Logo1ClinIcon size={64} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">Install on iPhone/iPad</h3>
                  <div className="text-left bg-slate-50 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-teal-700 text-sm font-bold">1</span>
                      </div>
                      <p className="text-slate-700">Tap the <strong>Share</strong> button <Share2 className="w-4 h-4 inline text-blue-500" /> in Safari</p>
                    </div>
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-teal-700 text-sm font-bold">2</span>
                      </div>
                      <p className="text-slate-700">Scroll down and tap <strong>"Add to Home Screen"</strong></p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-teal-700 text-sm font-bold">3</span>
                      </div>
                      <p className="text-slate-700">Tap <strong>"Add"</strong> to install</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500">Note: Use Safari browser for best results</p>
                </div>
              ) : (
                <div className="text-center">
                  <div className="flex justify-center mb-4">
                    <Logo1ClinIcon size={64} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">
                    Install on {isAndroid ? 'Android' : 'Your Device'}
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Click below to add 1Clin to your {isAndroid ? 'home screen' : 'desktop'}.
                  </p>
                  {deferredPrompt ? (
                    <Button 
                      onClick={handleInstall}
                      disabled={installStarted}
                      className="w-full bg-teal-600 hover:bg-teal-700 h-12 text-lg"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      {installStarted ? 'Installing...' : 'Install App'}
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-slate-600 text-sm bg-slate-50 p-3 rounded-lg">
                        <Chrome className="w-5 h-5 text-blue-500" />
                        <span>Look for the install icon in your browser's address bar</span>
                      </div>
                      <Button 
                        onClick={() => window.location.href = '/'}
                        variant="outline"
                        className="w-full"
                      >
                        Continue to App <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Features Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {features.map((feature, i) => (
              <div key={`el-${i}`} className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                <div className="text-3xl mb-2">{feature.icon}</div>
                <h4 className="font-semibold text-white text-sm">{feature.title}</h4>
                <p className="text-slate-400 text-xs mt-1">{feature.desc}</p>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="text-center mt-12">
            <p className="text-slate-400 text-sm">
              1clin.com • AI-Powered Clinic Management for India
            </p>
            <div className="flex items-center justify-center gap-4 mt-4">
              <a href="/" className="text-teal-400 hover:text-teal-300 text-sm">
                Open Web App
              </a>
              <span className="text-slate-600">•</span>
              <a href="/doctor/login" className="text-teal-400 hover:text-teal-300 text-sm">
                Doctor Login
              </a>
              <span className="text-slate-600">•</span>
              <a href="/vendor/login" className="text-teal-400 hover:text-teal-300 text-sm">
                Vendor Login
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
